import { useEffectEvent } from 'react'
import { useEffect, useRef, useState } from '../lib/teact/teact'
import { logger } from '../sentry'
import buildClassName from '../util/buildClassName'

const CLOSE_DURATION = 350

const useShowTransition = (
  isOpen = false,
  onCloseTransitionEnd?: () => void,
  noFirstOpenTransition = false,
  className: string | false = 'fast',
  noCloseTransition = false,
  closeDuration = CLOSE_DURATION,
  noOpenTransition = false,
) => {
  const closeTimeoutRef = useRef<number>(undefined)
  const hasOpenedRef = useRef(false)
  // СSS class should be added in a separate tick to turn on CSS transition.
  const [hasOpenClassName, setHasOpenClassName] = useState(isOpen && noFirstOpenTransition)
  const [isClosing, setIsClosing] = useState(false)

  const onHide = useEffectEvent(() => {
    const exec = () => {
      onCloseTransitionEnd?.()

      closeTimeoutRef.current = undefined
      setIsClosing(false)
    }

    if (noCloseTransition) {
      exec()
    } else {
      setIsClosing(true)
      closeTimeoutRef.current = window.setTimeout(exec, closeDuration)
    }
  })

  useEffect(() => {
    setHasOpenClassName(isOpen)

    if (isOpen) {
      hasOpenedRef.current = true
      setIsClosing(false)

      if (closeTimeoutRef.current) {
        window.clearTimeout(closeTimeoutRef.current)
        closeTimeoutRef.current = undefined
      }
    } else {
      if (closeTimeoutRef.current) {
        logger.warn('useShowTransition: close transition is already running, skipping')
        return
      }

      // Mounting in the closed state should not run a close cycle
      if (!hasOpenedRef.current) {
        return
      }

      onHide()
    }
  }, [isOpen])

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        window.clearTimeout(closeTimeoutRef.current)
      }
    }
  }, [])

  // `noCloseTransition`, when set to true, should remove the open class immediately
  const shouldHaveOpenClassName = (hasOpenClassName && !(noCloseTransition && !isOpen)) || (noOpenTransition && isOpen)
  const shouldRender = hasOpenClassName || isOpen || isClosing
  const transitionClassNames = buildClassName(
    className && 'opacity-transition',
    className,
    shouldHaveOpenClassName && 'open',
    !shouldHaveOpenClassName && 'not-open',
    shouldRender && 'shown',
    !shouldRender && 'not-shown',
    isClosing && 'closing',
  )

  return {
    shouldRender,
    transitionClassNames,
    hasShownClass: shouldRender,
    hasOpenClass: shouldHaveOpenClassName,
  }
}

export default useShowTransition
